import type { Bot, Position, Trade, Session, MarketIndex, ExecutionTapeEntry, InfraHealth } from '@/types/bot';

// --- Bots across 3 strategy groups ---
export const mockBots: Bot[] = [
  {
    id: 'bot-niftybees',
    name: 'NiftyBees',
    symbol: 'NIFTYBEES-EQ',
    token: '1044',
    broker: 'prostocks',
    tier: 'tier1',
    status: 'active',
    mode: 'monitoring',
    market: 'India',
    strategyGroup: 'iNAV Reversion',
    strategy: {
      buyThreshold: -0.10,
      sellThreshold: -0.03,
      positionSize: 15000,
      maxPositions: 5,
    },
    performance: {
      totalPnl: 15774.54,
      todayPnl: 5919.83,
      winRate: 74.2,
      totalTrades: 21240,
      activePositions: 13,
      fills: 7387,
    },
    lastUpdate: new Date().toISOString(),
    createdAt: '2026-01-15T09:00:00Z',
  },
  {
    id: 'bot-silverbees',
    name: 'SilverBees',
    symbol: 'SILVERBEES-EQ',
    token: '8080',
    broker: 'prostocks',
    tier: 'tier1',
    status: 'active',
    mode: 'monitoring',
    market: 'India',
    strategyGroup: 'iNAV Reversion',
    strategy: {
      buyThreshold: -0.15,
      sellThreshold: -0.05,
      positionSize: 5000,
      maxPositions: 5,
    },
    performance: {
      totalPnl: 9416.33,
      todayPnl: 9416.33,
      winRate: 68.5,
      totalTrades: 8430,
      activePositions: 8,
      fills: 3215,
    },
    lastUpdate: new Date().toISOString(),
    createdAt: '2026-01-10T09:00:00Z',
  },
  {
    id: 'bot-goldbees',
    name: 'GoldBees',
    symbol: 'GOLDBEES-EQ',
    token: '1047',
    broker: 'prostocks',
    tier: 'tier1',
    status: 'active',
    mode: 'monitoring',
    market: 'India',
    strategyGroup: 'iNAV Reversion',
    strategy: {
      buyThreshold: -0.20,
      sellThreshold: -0.08,
      positionSize: 10000,
      maxPositions: 3,
    },
    performance: {
      totalPnl: -175.25,
      todayPnl: -175.25,
      winRate: 52.3,
      totalTrades: 4120,
      activePositions: 2,
      fills: 1840,
    },
    lastUpdate: new Date().toISOString(),
    createdAt: '2026-01-12T09:00:00Z',
  },
  {
    id: 'bot-tqqq',
    name: 'TQQQ',
    symbol: 'TQQQ',
    token: '9999',
    broker: 'ibkr',
    tier: 'tier1',
    status: 'active',
    mode: 'monitoring',
    market: 'US',
    strategyGroup: 'Pairs Trading',
    strategy: {
      buyThreshold: -0.03,
      sellThreshold: -0.01,
      positionSize: 25000,
      maxPositions: 5,
    },
    performance: {
      totalPnl: 13946.38,
      todayPnl: 13946.38,
      winRate: 81.4,
      totalTrades: 3757,
      activePositions: 5,
      fills: 2890,
    },
    lastUpdate: new Date().toISOString(),
    createdAt: '2026-02-01T14:00:00Z',
  },
  {
    id: 'bot-momentum',
    name: 'MomentumAlpha',
    symbol: 'SPY',
    token: '5555',
    broker: 'ibkr',
    tier: 'tier2',
    status: 'paused',
    mode: 'simulation',
    market: 'US',
    strategyGroup: 'Momentum',
    strategy: {
      buyThreshold: -0.05,
      sellThreshold: -0.02,
      positionSize: 20000,
      maxPositions: 3,
    },
    performance: {
      totalPnl: 0,
      todayPnl: 0,
      winRate: 0,
      totalTrades: 0,
      activePositions: 0,
      fills: 0,
    },
    lastUpdate: new Date(Date.now() - 86400000).toISOString(),
    createdAt: '2026-03-01T14:00:00Z',
  },
];

// --- Market Indices ---
export const mockMarketIndices: MarketIndex[] = [
  { name: 'NIFTY', value: 64129.99, change: -112.45, changePercent: -0.18, direction: 'down' },
  { name: 'NASDAQ', value: 16177.32, change: 42.18, changePercent: 0.26, direction: 'up' },
];

// --- Infrastructure Health ---
export const mockInfraHealth: InfraHealth = {
  networkLatencyMs: 3.75,
  syncStatus: 'OK',
};

// --- Execution Tape ---
const symbols = ['SILVERBEES', 'GOLDBEES', 'NIFTYBEES', 'TQQQ'];
const exchanges = ['NSE', 'NSE', 'NSE', 'NASDAQ'];
const basePrices: Record<string, number> = {
  SILVERBEES: 92.45,
  GOLDBEES: 63.25,
  NIFTYBEES: 241.30,
  TQQQ: 64.12,
};

function randomId(): string {
  return Math.random().toString(36).substring(2, 10);
}

function makeTimestamp(offsetMs: number): string {
  return new Date(Date.now() - offsetMs).toISOString();
}

export const mockExecutionTape: ExecutionTapeEntry[] = Array.from({ length: 20 }, (_, i) => {
  const symbolIdx = Math.floor(Math.random() * symbols.length);
  const sym = symbols[symbolIdx];
  const base = basePrices[sym];
  return {
    id: randomId(),
    time: makeTimestamp((20 - i) * 3200),
    type: Math.random() > 0.5 ? 'BUY' : 'SELL',
    symbol: sym,
    qty: Math.floor(Math.random() * 200) + 10,
    price: +(base + (Math.random() - 0.5) * 0.4).toFixed(2),
    exchange: exchanges[symbolIdx],
  };
});

export function generateRandomExecution(): ExecutionTapeEntry {
  const symbolIdx = Math.floor(Math.random() * symbols.length);
  const sym = symbols[symbolIdx];
  const base = basePrices[sym];
  return {
    id: randomId(),
    time: new Date().toISOString(),
    type: Math.random() > 0.5 ? 'BUY' : 'SELL',
    symbol: sym,
    qty: Math.floor(Math.random() * 200) + 10,
    price: +(base + (Math.random() - 0.5) * 0.4).toFixed(2),
    exchange: exchanges[symbolIdx],
  };
}

// --- Positions (unchanged structure, updated botIds) ---
export const mockPositions: Position[] = [
  {
    id: 'pos-1',
    botId: 'bot-silverbees',
    symbol: 'SILVERBEES-EQ',
    entryPrice: 92.45,
    currentPrice: 92.78,
    quantity: 54,
    pnl: 17.82,
    pnlPercent: 0.36,
    openedAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'pos-2',
    botId: 'bot-goldbees',
    symbol: 'GOLDBEES-EQ',
    entryPrice: 63.25,
    currentPrice: 63.45,
    quantity: 158,
    pnl: 31.60,
    pnlPercent: 0.32,
    openedAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// --- Trades ---
export const mockTrades: Trade[] = [
  {
    id: 'trade-1',
    botId: 'bot-silverbees',
    symbol: 'SILVERBEES-EQ',
    type: 'buy',
    quantity: 54,
    price: 92.45,
    executedAt: new Date(Date.now() - 7200000).toISOString(),
    status: 'executed',
  },
  {
    id: 'trade-2',
    botId: 'bot-silverbees',
    symbol: 'SILVERBEES-EQ',
    type: 'sell',
    quantity: 54,
    price: 92.89,
    pnl: 23.76,
    executedAt: new Date(Date.now() - 3600000).toISOString(),
    status: 'executed',
  },
  {
    id: 'trade-3',
    botId: 'bot-goldbees',
    symbol: 'GOLDBEES-EQ',
    type: 'buy',
    quantity: 158,
    price: 63.25,
    executedAt: new Date(Date.now() - 10800000).toISOString(),
    status: 'executed',
  },
];

// --- Sessions ---
export const mockSessions: Session[] = [
  {
    id: 'session-1',
    botId: 'bot-silverbees',
    startTime: new Date(Date.now() - 28800000).toISOString(),
    endTime: undefined,
    pnl: 850.25,
    trades: 14,
    status: 'active',
  },
];
